"use client";

// Finance showcase — alternating text/preview rows that slide in from each side.
// Each row pairs a short value block with a real-terminal screen recording
// (muted autoplay-loop MP4, plays on scroll-in). Some clips are montages so a
// single row can show several screens. Built on motion/react (already a dep).

import { useRef } from "react";
import { INDICATOR_CLAIM, INSTRUMENT_CLAIM } from "@/lib/finance-markets";
import { motion, useInView } from "motion/react";
import { ClipVideo } from "@/components/marketing/clip-video";
import {
  LayoutGrid,
  Search,
  CandlestickChart,
  SlidersHorizontal,
  FileText,
  FlaskConical,
  Check,
} from "lucide-react";

type Row = {
  icon: typeof LayoutGrid;
  name: string;
  tagline: string;
  bullets: string[];
  src: string;
};

const ROWS: Row[] = [
  {
    icon: LayoutGrid,
    name: "The whole market",
    tagline: "See the entire market at a glance.",
    bullets: [
      "Today's board — indices, movers and breadth in one view.",
      "A heatmap of the whole market, green-to-red on the day.",
      "Global indices and your charts wall, side by side.",
    ],
    src: "/finance/gifs/finance-markets.mp4",
  },
  {
    icon: Search,
    name: "Every name, in depth",
    tagline: "Stock detail without the digging.",
    bullets: [
      "Live quote, order book and time & sales.",
      "An AI read of every name, in plain English.",
      "Valuation and the numbers that matter, explained.",
    ],
    src: "/finance/gifs/finance-snapshot.mp4",
  },
  {
    icon: CandlestickChart,
    name: "Pro charts & indicators",
    tagline: "Charts the way a desk reads them.",
    bullets: [
      `Real-time, ${INDICATOR_CLAIM} indicators, any timeframe.`,
      "A forecast cone with honest confidence ranges.",
      "Drawing tools and multi-pane studies.",
    ],
    src: "/finance/gifs/finance-chart.mp4",
  },
  {
    icon: SlidersHorizontal,
    name: "Options, bonds & the tape",
    tagline: "Go deeper than the price.",
    bullets: [
      "The whole options chain and dealer gamma.",
      "Issuer debt and reference curves.",
      "Level 2 depth and the live tape.",
    ],
    src: "/finance/gifs/finance-stockdeep.mp4",
  },
  {
    icon: FileText,
    name: "Filings & earnings calls",
    tagline: "Read the actual documents.",
    bullets: [
      "SEC filings rendered inline, no downloads.",
      "Earnings-call transcripts, speaker by speaker.",
      "Then ask the terminal to summarise them.",
    ],
    src: "/finance/gifs/finance-research.mp4",
  },
  {
    icon: FlaskConical,
    name: "Screen, crypto & backtest",
    tagline: "From idea to tested strategy.",
    bullets: [
      `Screen ${INSTRUMENT_CLAIM} instruments by your rules.`,
      "Crypto and FX, around the clock.",
      "Backtest a strategy and watch the equity curve.",
    ],
    src: "/finance/gifs/finance-tools.mp4",
  },
];

function Preview({ src }: { src: string }) {
  return (
    <div className="lift-card relative overflow-hidden rounded-2xl border hairline-strong bg-card shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
      <div className="flex items-center gap-1.5 px-3 h-7 border-b hairline bg-foreground/[0.02]">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
        ))}
      </div>
      {/* ClipVideo sizes its box to the clip's real aspect → fills, never crops. */}
      <ClipVideo src={src} />
    </div>
  );
}

function FeatureRow({ row, index }: { row: Row; index: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reversed = index % 2 === 1;
  const Icon = row.icon;
  const words = row.tagline.split(" ");

  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
      {/* Text */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? 40 : -40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className={reversed ? "lg:order-2" : ""}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border hairline-strong text-foreground/80">
            <Icon className="h-4 w-4" />
          </span>
          <span className="kicker">{row.name}</span>
        </div>
        <h3 className="mt-4 text-2xl md:text-3xl font-medium tracking-tight">
          {words.slice(0, -2).join(" ")}{" "}
          <span className="font-serif italic">{words.slice(-2).join(" ")}</span>
        </h3>
        <ul className="mt-5 space-y-3">
          {row.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-foreground/70">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                <Check className="h-3 w-3" />
              </span>
              <span className="text-sm leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Preview clip — slides in from the opposite side */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? -40 : 40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 }}
        className={reversed ? "lg:order-1" : ""}
      >
        <Preview src={row.src} />
      </motion.div>
    </div>
  );
}

export function FinanceFeaturesAlternating() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section bg-foreground/[0.015] border-y hairline">
      <div className="container-x">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="eyebrow text-accent">The terminal, end to end</p>
          <h2 className="display-text mt-3 text-[clamp(1.8rem,3.6vw,2.8rem)] tracking-tight">
            Everything in one <span className="font-serif italic">workspace.</span>
          </h2>
          <p className="mt-3 text-foreground/65 text-base md:text-lg">
            Real screens from the live terminal — the whole market, any name in depth, and the
            tools to act, all moving in real time.
          </p>
        </motion.div>

        <div className="mt-12 space-y-16 md:space-y-20">
          {ROWS.map((row, i) => (
            <FeatureRow key={row.name} row={row} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
